import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconAdd } from "blocks-nextjs/blocks-react/components/Icons/Add/Add";
import { CatIconCustomer } from "blocks-nextjs/blocks-react/components/Icons/Customer/Customer";
import { CatIconTrash } from "blocks-nextjs/blocks-react/components/Icons/Trash/Trash";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaasRoute } from "../../routes";
import { useGetProjectDetailsQuery } from "../../services/query/create-project-api.query";
import { useUpdateProjectAssetDetails } from "../../services/query/updates-project-asset-details.query";
import useGlobalStore from "../../store/useGlobalStore";
import useNewProjectStore from "../../store/useNewProjectStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
import AddAssetDrawer from "./AddAssetDrawer";
import { ASSET_SOURCE_TYPE_CHARGING_STATION, ASSET_SOURCE_TYPE_ENERGY_SOURCE, AssetType } from "./AddAssetsListCard";
import NewProjectModal from "./NewProjectModal";
const isSameAsset = (a, b) => a.assetSourceId === b.assetSourceId &&
    a.assetSerialNumber === b.assetSerialNumber &&
    a.assetSourceType === b.assetSourceType;
export const NON_CAT_ASSET_TARGET = "NON_CAT";
const CustomerAssetPortfolio = ({ activeStep, onAddCustomerAsset, onAddNonCatAsset, setActiveStepHandler }) => {
    const navigate = useNavigate();
    const { customerName, primaryPartyType, setGlobalNotification, userPartyNumber } = useGlobalStore((state) => ({
        customerName: state.customerName,
        primaryPartyType: state.primaryPartyType,
        setGlobalNotification: state.setGlobalNotification,
        userPartyNumber: state.userPartyNumber
    }));
    const { projectId } = useNewProjectStore((state) => ({
        projectId: state.projectId
    }));
    const [isCancelModalActive, setIsCancelModalActive] = useState(false);
    const [addAssetTarget, setAddAssetTarget] = useState(null);
    const [customerAssets, setCustomerAssets] = useState([]);
    const [nonCatAssets, setNonCatAssets] = useState([]);
    const projectQueryParams = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const { data: projectDetailsResponse, refetch: fetchProjectDetails } = useGetProjectDetailsQuery(projectId, projectQueryParams);
    const { isPending: isAddingAssets, mutate: addAssetsMutate } = useUpdateProjectAssetDetails(projectId, {
        ...projectQueryParams,
        operationType: "create"
    });
    const { isPending: isRemovingAssets, mutate: removeAssetsMutate } = useUpdateProjectAssetDetails(projectId, {
        ...projectQueryParams,
        operationType: "remove"
    });
    useEffect(() => {
        if (projectId) {
            fetchProjectDetails();
        }
    }, [projectId, fetchProjectDetails]);
    const projectAssets = useMemo(() => (projectDetailsResponse?.assetDetails ?? [])
        .filter((asset) => asset.assetSourceType?.toUpperCase() !== ASSET_SOURCE_TYPE_CHARGING_STATION &&
        asset.assetSourceType?.toUpperCase() !== ASSET_SOURCE_TYPE_ENERGY_SOURCE)
        .map((asset) => ({
        assetId: asset.assetId,
        assetMetadata: {
            model: asset.assetMetadata?.model,
            modelYear: asset.assetMetadata?.modelYear,
            productFamily: {
                code: asset.assetMetadata?.productFamily?.code,
                iconUrl: asset.assetMetadata?.productFamily?.iconUrl,
                name: asset.assetMetadata?.productFamily?.name
            }
        },
        assetName: asset.assetName,
        assetSerialNumber: asset.assetSerialNumber ?? "",
        assetSourceId: asset.assetSourceId,
        assetSourceType: asset.assetSourceType?.toUpperCase()
    })), [projectDetailsResponse]);
    useEffect(() => {
        setCustomerAssets(projectAssets);
    }, [projectAssets]);
    const portfolioCustomers = customerName ? [{ ccid: userPartyNumber, name: customerName }] : [];
    const handleAddAssets = (assets) => {
        if (addAssetTarget === NON_CAT_ASSET_TARGET) {
            setNonCatAssets(assets);
            onAddNonCatAsset?.(assets);
        }
        else if (addAssetTarget) {
            setCustomerAssets(assets);
            onAddCustomerAsset?.(addAssetTarget, assets);
        }
        setAddAssetTarget(null);
    };
    const removeAsset = (assets, assetSourceId) => assets.filter((asset) => asset.assetSourceId !== assetSourceId);
    const handleNext = () => {
        const selectedAssets = [...customerAssets, ...nonCatAssets];
        const addedAssets = selectedAssets.filter((asset) => !projectAssets.some((projectAsset) => isSameAsset(projectAsset, asset)));
        const removedAssets = projectAssets.filter((projectAsset) => !selectedAssets.some((asset) => isSameAsset(projectAsset, asset)));
        const hasAddedAssets = addedAssets.length > 0;
        const hasRemovedAssets = removedAssets.length > 0;
        if (!projectId || (!hasAddedAssets && !hasRemovedAssets)) {
            navigate(CaasRoute.Home);
            return;
        }
        const getSuccessMessage = () => {
            if (hasAddedAssets && hasRemovedAssets) {
                return "Success. Assets have been added and removed successfully";
            }
            return hasAddedAssets
                ? "Success. Assets have been added successfully"
                : "Success. Assets have been removed successfully";
        };
        const getErrorMessage = () => {
            if (hasAddedAssets && hasRemovedAssets) {
                return "Error. Failed to add and remove assets. Please try later.";
            }
            return hasAddedAssets
                ? "Error. Failed to add assets. Please try later."
                : "Error. Failed to remove assets. Please try later.";
        };
        const notifyError = () => {
            setGlobalNotification({
                message: getErrorMessage(),
                showNotification: true,
                type: Variant.error
            });
        };
        const notifySuccessAndLeave = () => {
            setGlobalNotification({
                message: getSuccessMessage(),
                showNotification: true,
                type: Variant.success
            });
            navigate(CaasRoute.Home);
        };
        const addRequest = {
            assetDetails: addedAssets.map((asset) => ({
                assetMetadata: {
                    model: asset.assetMetadata?.model,
                    modelYear: asset.assetMetadata?.modelYear,
                    productFamily: {
                        code: asset.assetMetadata?.productFamily?.code,
                        iconUrl: asset.assetMetadata?.productFamily?.iconUrl,
                        name: asset.assetMetadata?.productFamily?.name
                    }
                },
                assetName: asset.assetName ?? asset.assetSerialNumber,
                assetSerialNumber: asset.assetSerialNumber,
                assetSourceId: asset.assetSourceId,
                assetSourceType: asset.assetSourceType ?? AssetType.MACHINE
            }))
        };
        const removeRequest = {
            assetDetails: removedAssets.map((asset) => ({
                assetId: asset.assetId
            }))
        };
        if (!hasAddedAssets) {
            removeAssetsMutate(removeRequest, { onError: notifyError, onSuccess: notifySuccessAndLeave });
            return;
        }
        addAssetsMutate(addRequest, {
            onError: notifyError,
            onSuccess: () => {
                if (!hasRemovedAssets) {
                    notifySuccessAndLeave();
                    return;
                }
                removeAssetsMutate(removeRequest, { onError: notifyError, onSuccess: notifySuccessAndLeave });
            }
        });
    };
    const renderAssetList = (assets, onRemove, scope) => {
        if (assets.length === 0) {
            return null;
        }
        return (_jsx("ul", { className: "flex flex-wrap gap-[16px] list-none m-0 p-0", "data-test": `${scope}-asset-list`, children: assets.map((asset) => (_jsxs("li", { className: "flex items-center gap-[12px] w-[315px] h-[56px] pl-[8px] pr-[8px] rounded bg-[#F2F2F2]", "data-test": `${scope}-asset-${asset.assetSourceId}`, children: [asset.assetMetadata?.productFamily?.iconUrl && (_jsx("img", { alt: "", className: "w-[48px] h-[32px] object-contain shrink-0", "data-test": `${scope}-asset-image-${asset.assetSourceId}`, src: asset.assetMetadata.productFamily.iconUrl })), _jsxs("div", { className: "flex-1 flex flex-col min-w-0 break-words", children: [_jsx("span", { className: "font-semibold text-base leading-6 text-black", "data-test": `${scope}-asset-name-${asset.assetSourceId}`, children: asset.assetName }), _jsx("span", { className: "font-semibold text-xs leading-4 text-[#3F3F3F] truncate", "data-test": `${scope}-asset-serial-${asset.assetSourceId}`, children: [asset.assetSerialNumber, asset.assetMetadata?.model].filter(Boolean).join(" - ") })] }), _jsx(CatButton, { "data-test": `${scope}-asset-remove-${asset.assetSourceId}`, hideText: true, onClick: () => onRemove(asset.assetSourceId), size: "sm", variant: "ghost", children: _jsxs(React.Fragment, { children: ["Remove", _jsx(CatIconTrash, { color: "error", slot: "after" })] }, ".0") })] }, asset.assetSourceId))) }));
    };
    return (_jsxs("div", { className: "flex-1 flex flex-col gap-[24px]", "data-test": "customer-asset-portfolio", children: [_jsxs("div", { className: "flex flex-col gap-[12px] p-[16px] rounded bg-[#F2F2F2] border border-[#CCCCCC]", children: [_jsx("div", { className: "flex items-center pb-[8px]", children: _jsx(CatHeading, { "data-test": "customer-asset-portfolio-title", variant: "title-sm", children: "Customer & Asset Portfolio" }) }), _jsx("div", { className: "flex flex-col gap-[8px]", children: portfolioCustomers.map((customer) => (_jsxs("div", { className: "flex flex-col rounded-lg bg-white shadow-[0px_1px_4px_0px_rgba(0,0,0,0.2)]", "data-test": "customer-portfolio-card", children: [_jsxs("div", { className: "flex items-center gap-[16px] px-[16px] py-[8px]", children: [_jsx("div", { className: "flex items-center p-[12px] rounded-full bg-[#FFCD11]", children: _jsx(CatIconCustomer, { "data-test": "customer-portfolio-card-icon" }) }), _jsxs("div", { className: "flex-1 flex flex-col min-w-0 text-black", children: [_jsx(CatHeading, { "data-test": "customer-portfolio-card-name", variant: "title-sm", children: customer.name }), _jsxs("span", { className: "font-body text-base font-normal leading-6", "data-test": "customer-portfolio-card-ccid", children: ["CCID: ", customer.ccid] })] }), _jsx(CatButton, { "data-test": "customer-portfolio-add-asset-button", onClick: () => setAddAssetTarget(customer), size: "sm", variant: "ghost", children: _jsxs(React.Fragment, { children: [_jsx(CatIconAdd, { slot: "before" }), "Add Asset"] }, ".0") })] }), customerAssets.length > 0 && (_jsx("div", { className: "px-[16px] pb-[16px] pt-[8px] border-t border-[#E6E6E6]", children: renderAssetList(customerAssets, (assetSourceId) => setCustomerAssets((assets) => removeAsset(assets, assetSourceId)), "customer") }))] }, customer.ccid))) })] }), _jsxs("div", { className: "flex flex-col gap-[16px] p-[16px] rounded bg-[#F2F2F2] border border-[#CCCCCC]", children: [_jsxs("div", { className: "flex items-center justify-between gap-[16px]", children: [_jsx(CatHeading, { "data-test": "non-cat-assets-title", variant: "title-sm", children: "Non Cat Assets" }), _jsx(CatButton, { "data-test": "non-cat-assets-add-asset-button", onClick: () => setAddAssetTarget(NON_CAT_ASSET_TARGET), size: "md", variant: "ghost", children: _jsxs(React.Fragment, { children: [_jsx(CatIconAdd, { slot: "before" }), "Add Asset"] }, ".0") })] }), renderAssetList(nonCatAssets, (assetSourceId) => setNonCatAssets((assets) => removeAsset(assets, assetSourceId)), "non-cat")] }), _jsx("div", { className: "flex-1 flex items-end justify-end", children: _jsxs("div", { className: "flex gap-[8px]", children: [_jsx(CatButton, { "data-test": "cancel-customer-asset-portfolio-button", onClick: () => setIsCancelModalActive(true), size: "md", children: "Cancel" }), _jsx(CatButton, { "data-test": "back-customer-asset-portfolio-button", onClick: () => setActiveStepHandler(activeStep - 1), size: "md", children: "Back" }), _jsx(CatButton, { "data-test": "next-customer-asset-portfolio-button", disabled: isAddingAssets || isRemovingAssets, onClick: handleNext, size: "md", variant: "primary", children: "Next" })] }) }), _jsx(NewProjectModal, { onModalClose: setIsCancelModalActive, showModal: isCancelModalActive }), _jsx(AddAssetDrawer, { isActive: addAssetTarget !== null, isNonCatAsset: addAssetTarget === NON_CAT_ASSET_TARGET, onAddAssets: handleAddAssets, onClose: () => setAddAssetTarget(null), preSelectedAssets: addAssetTarget === NON_CAT_ASSET_TARGET ? nonCatAssets : customerAssets })] }));
};
export default CustomerAssetPortfolio;
